// Horizontal strip of iteration thumbnails — current image + ancestors.
//
// Behaviour:
//   - Shows the current image (from props) at the head of the strip.
//   - Walks back through `parent_id` via repeated `/api/catalogue/:id/load`
//     calls (the safe fallback when Hub's `lineage` field is absent on
//     the load response).
//   - Clicking any ancestor fires `onPick(ancestor)` so Studio can update
//     current image / seed / parameters.

import { useEffect, useState } from 'react';
import { api, ApiError, type CatalogueLineageEntry, type CatalogueLoadResponse } from '../api/client';
import type { Parameters } from '../types/parameters';
import { useToast } from './Toast';
import { IconLayers, IconSpinner } from './Icon';

export interface LineageStripProps {
  /** The id of the currently displayed image (or null while loading). */
  currentImageId: string | null;
  /** Thumbnail URL for the current image. */
  currentThumbnailUrl?: string | null;
  /** Called when mum clicks an ancestor (NOT the current image). */
  onPick: (entry: CatalogueLineageEntry) => void;
  /** Maximum number of ancestors to walk (default 6 per spec). */
  maxDepth?: number;
}

interface StripEntry extends CatalogueLineageEntry {
  /** Local display order — `0` = direct parent, increasing for grandparents. */
  depth: number;
}

export function LineageStrip(props: LineageStripProps) {
  const { currentImageId, currentThumbnailUrl, onPick, maxDepth = 6 } = props;
  const [chain, setChain] = useState<StripEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    async function walk(): Promise<void> {
      if (!currentImageId) {
        setChain([]);
        return;
      }
      setLoading(true);
      setError(null);
      const collected: StripEntry[] = [];
      try {
        // Load the current row first — its parent_id tells us where to
        // walk next. Track seen ids to be defensive against cycles.
        let cursorId: string | null = currentImageId;
        const seen = new Set<string>();
        let depth = 0;
        while (cursorId && depth < maxDepth && !seen.has(cursorId)) {
          seen.add(cursorId);
          // eslint-disable-next-line no-await-in-loop
          const row: CatalogueLoadResponse | null = await api
            .loadCatalogueItem(cursorId)
            .catch((e: unknown) => {
              // 404 means this row isn't a catalogue entry yet (we're
              // still in the freshly-generated space). Treat that as
              // "no further ancestors".
              if (e instanceof ApiError && e.status === 404) return null;
              throw e;
            });
          if (!row) break;
          if (depth === 0) {
            // First iteration = the current row; just record the parent.
            cursorId = row.parent_id ?? null;
            depth++;
            continue;
          }
          // Otherwise this row is an ancestor — add it to the chain.
          collected.push({
            id: row.id,
            image_url: row.image_url,
            thumbnail_url: row.thumbnail_url,
            seed: row.seed,
            parameters: row.parameters as Parameters,
            depth,
          });
          cursorId = row.parent_id ?? null;
          depth++;
        }
        if (!cancelled) setChain(collected);
      } catch (e) {
        if (!cancelled) {
          const msg =
            e instanceof ApiError ? e.message : 'Could not load lineage';
          setError(msg);
          toast.error('Could not load ancestry', msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void walk();
    return () => {
      cancelled = true;
    };
  }, [currentImageId, maxDepth, toast]);

  if (!currentImageId) return null;
  if (!loading && chain.length === 0 && !error) return null;

  return (
    <div
      role="region"
      aria-label="Iteration ancestry"
      className="rounded-lg border border-neutral-300 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-900"
    >
      <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        <IconLayers size={14} aria-hidden />
        <span>Ancestry</span>
        {loading && (
          <IconSpinner size={12} className="animate-spin" aria-hidden />
        )}
        <span className="sr-only">{loading ? 'Loading ancestry' : ''}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {/* Current image — shown as a non-clickable "Now" marker. */}
        <div
          aria-current="true"
          aria-label="Current eye"
          className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 border-accent"
        >
          {currentThumbnailUrl ? (
            <img
              src={currentThumbnailUrl}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full bg-neutral-200 dark:bg-neutral-800" />
          )}
          <span className="absolute right-0 top-0 rounded-bl-md bg-accent px-1.5 text-[10px] font-bold uppercase text-white">
            Now
          </span>
        </div>
        {chain.map((entry) => (
          <button
            key={`${entry.id}-${entry.depth}`}
            type="button"
            onClick={() => onPick(entry)}
            className="group relative h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-md border border-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700"
            aria-label={`Go back to iteration ${entry.depth} (id ${entry.id.slice(0, 6)})`}
            title={`Step back ${entry.depth}`}
          >
            <img
              src={entry.thumbnail_url || entry.image_url}
              alt=""
              className="h-full w-full object-cover transition-opacity duration-150 group-hover:opacity-80"
              loading="lazy"
            />
            <span className="absolute right-0 top-0 rounded-bl-md bg-neutral-900/80 px-1.5 text-[10px] font-bold uppercase text-white">
              −{entry.depth}
            </span>
          </button>
        ))}
      </div>
      {error && (
        <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">
          {error}
        </p>
      )}
    </div>
  );
}
